<template>
  <section id="advantages" class="advantages bg-decoration">
    <div class="container">
      <div class="advantages-header">
        <h2 class="advantages-title clickable" @click="goToAdvantages">{{ $t('advantages.title') }}</h2>
      </div>
      <div class="advantages-content">
        <div class="advantage-item glass glass-glow" v-for="i in 3" :key="i">
          <div class="advantage-icon glass-sm">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path v-if="i === 1" d="M20 18a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2"/>
              <path v-if="i === 1" d="M9 15a3.5 3.5 0 0 1 5.5-2.97A3.5 3.5 0 0 1 16 15"/>
              <circle v-if="i === 1" cx="12" cy="8" r="4"/>
              <path v-if="i === 2" d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
              <path v-if="i === 2" d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
              <path v-if="i === 3" d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
          </div>
          <h3 class="advantage-heading">{{ $t(`advantages.advantage${i}.title`) }}</h3>
          <ul class="advantage-list">
            <li v-for="(item, index) in $tm(`advantages.advantage${i}.items`)" :key="index">
              {{ item }}
            </li>
          </ul>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { useRouter } from 'vue-router'

const router = useRouter()

const goToAdvantages = () => {
  router.push('/advantages/overview')
}
</script>

<style scoped>
.advantages {
  padding: 100px 0;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  position: relative;
}

.advantages::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background:
    radial-gradient(circle at 50% 0%, rgba(52, 152, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 0% 100%, rgba(15, 39, 64, 0.05) 0%, transparent 50%);
  pointer-events: none;
}

@media (max-width: 1366px) {
  .advantages {
    padding: 80px 0;
  }
}

@media (max-width: 1024px) {
  .advantages {
    padding: 60px 0;
  }
}

@media (max-width: 768px) {
  .advantages {
    padding: 40px 0;
  }
}

.advantages-header {
  text-align: center;
  margin-bottom: 60px;
  position: relative;
  z-index: 1;
}

@media (max-width: 1366px) {
  .advantages-header {
    margin-bottom: 50px;
  }
}

@media (max-width: 1024px) {
  .advantages-header {
    margin-bottom: 40px;
  }
}

@media (max-width: 768px) {
  .advantages-header {
    margin-bottom: 30px;
  }
}

.advantages-title {
  font-size: 2.2rem;
  font-weight: 700;
  color: var(--primary-color);
  margin: 0;
  position: relative;
  display: inline-block;
}

.advantages-title::after {
  content: '';
  position: absolute;
  bottom: -15px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 4px;
  background: linear-gradient(135deg, var(--secondary-color), var(--primary-color));
  border-radius: 2px;
}

.advantages-title.clickable {
  cursor: pointer;
  transition: color 0.3s ease;
}

.advantages-title.clickable:hover {
  color: var(--secondary-color);
}

@media (max-width: 1366px) {
  .advantages-title {
    font-size: 2rem;
  }
}

@media (max-width: 1024px) {
  .advantages-title {
    font-size: 1.8rem;
  }
}

@media (max-width: 768px) {
  .advantages-title {
    font-size: 1.5rem;
  }

  .advantages-title::after {
    width: 60px;
    bottom: -10px;
  }
}

.advantages-content {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  position: relative;
  z-index: 1;
}

@media (max-width: 1024px) {
  .advantages-content {
    grid-template-columns: 1fr 1fr;
    gap: 24px;
  }
}

@media (max-width: 768px) {
  .advantages-content {
    grid-template-columns: 1fr;
    gap: 20px;
  }
}

.advantage-item {
  padding: 40px 32px;
  border-radius: 24px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.advantage-item:hover {
  transform: translateY(-10px);
}

@media (max-width: 1366px) {
  .advantage-item {
    padding: 35px 28px;
  }
}

@media (max-width: 1024px) {
  .advantage-item {
    padding: 32px 24px;
  }
}

@media (max-width: 768px) {
  .advantage-item {
    padding: 28px 22px;
  }
}

.advantage-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, rgba(52, 152, 255, 0.12) 0%, rgba(15, 39, 64, 0.06) 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  color: var(--secondary-color);
  box-shadow: 0 4px 15px rgba(52, 152, 255, 0.12);
}

@media (max-width: 1366px) {
  .advantage-icon {
    width: 72px;
    height: 72px;
    margin-bottom: 20px;
  }
}

@media (max-width: 1024px) {
  .advantage-icon {
    width: 64px;
    height: 64px;
    margin-bottom: 18px;
  }

  .advantage-icon svg {
    width: 32px;
    height: 32px;
  }
}

@media (max-width: 768px) {
  .advantage-icon {
    width: 60px;
    height: 60px;
    margin-bottom: 16px;
  }
}

.advantage-heading {
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--primary-color);
  margin: 0 0 18px 0;
  position: relative;
  padding-bottom: 14px;
}

.advantage-heading::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 50px;
  height: 3px;
  background: linear-gradient(90deg, var(--secondary-color), #5fa8ff);
  border-radius: 2px;
}

@media (max-width: 1366px) {
  .advantage-heading {
    font-size: 1.2rem;
    margin-bottom: 15px;
    padding-bottom: 12px;
  }
}

@media (max-width: 1024px) {
  .advantage-heading {
    font-size: 1.15rem;
    margin-bottom: 12px;
    padding-bottom: 10px;
  }
}

@media (max-width: 768px) {
  .advantage-heading {
    font-size: 1.1rem;
  }
}

.advantage-list {
  list-style: none;
  padding: 0;
  margin: 0;
  color: var(--text-light);
  font-size: 0.95rem;
  line-height: 1.8;
}

.advantage-list li {
  position: relative;
  padding-left: 22px;
  margin-bottom: 10px;
}

.advantage-list li::before {
  content: '•';
  position: absolute;
  left: 0;
  color: var(--secondary-color);
  font-weight: bold;
  font-size: 1.2em;
  line-height: 1.5;
}

@media (max-width: 1366px) {
  .advantage-list {
    font-size: 0.92rem;
  }

  .advantage-list li {
    margin-bottom: 8px;
  }
}

@media (max-width: 1024px) {
  .advantage-list {
    font-size: 0.9rem;
  }

  .advantage-list li {
    margin-bottom: 6px;
  }
}

@media (max-width: 768px) {
  .advantage-list {
    font-size: 0.9rem;
  }
}
</style>
